/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import handler from '../../../../share/util/apiResultValidator';
import projectData from '../../../../share/components/vue/ProjectData';
import util from '../../../../share/util/utils';
import diagnoseResource from "../../../../share/util/diagnose";
import apiService from '../../../../share/util/api/service';
import shareVehicle from '../../../../share/util/vehicle';
import vinInfoUtils from "../../../../util/vinInfoUtils";

/**
 * 各フェーズ定義
 */
const PHASE = {
  INDEX_ACQUISITION: 'indexAcquisition',
  INDEX_DISPLAY: 'indexDisplay',
  PROJECT_DATA: 'projectData',
  DATA_ACQUISITION: 'dataAcquisition',
  DATA_RESULTS_DISPLAY: 'dataResultsDisplay',
  DATA_NON_EXISTENCE: 'dataNonExistence',
  ACQUISITION_FAILED: 'acquisitionFailed'
};

// チェックボックスのチェック制限数
const LIMIT_NUM = 2;

export default {
  name: 'requirement138',
  data() {
    return {
      phase: PHASE.INDEX_ACQUISITION,
      isLoading: true,
      items: [],
      systemId: '',
      existentDidList: [],
      isChecked: [],
      isDisabled: [],
      isButtonDisabled: true,
      checkedNumList: [],
      columns: [
        { name: 'check', label: '', useCheckbox: true },
        { name: 'number', label: this.i18nWsKey + '.table_column_number' },
        { name: 'period', label: this.i18nWsKey + '.table_column_period' }
      ],
      requestId: '',
      vin: null,                                                                     // データ保存画面のVIN/車両番号
      vehicleName: null,                                                             // データ保存画面の車両
      displayProjectID: '',                                                          // データ保存画面のID
      hideRegistrationNumber: true,                                                  // データ保存画面の登録番号表示フラグ
      registrationNumber: null,                                                      // データ保存画面の登録番号表示
      functionName: 'individual.individual_diagnosis_function_work_support',         // データ保存画面の機能名
      dataName: '',                                                                  // データ保存画面のデータ名
      memo: '',                                                                      // データ保存画面のメモ
      modal_requirement_138: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      netAppId: state => state.netAppConnector.pairingAppId,
      vehicleInfo: state => state.vehicle.vehicleInfo,
      vehicleModelDisplayNameList: state => state.masterResources.vehicleModelDisplayNameList,
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ACQUISITION_FAILED:
          return 'danger';
        case PHASE.DATA_RESULTS_DISPLAY:
        case PHASE.DATA_NON_EXISTENCE:
          return 'success';
        case PHASE.INDEX_ACQUISITION:
        case PHASE.INDEX_DISPLAY:
        case PHASE.DATA_ACQUISITION:
        case PHASE.PROJECT_DATA:
        default:
          return 'secondary';
      }
    },

    /**
     * ダウンロードが実行不可能
     */
    downloadDisabled() {
      return !this.checkedNumList.some(data => data.upload_status === 'completed');
    },

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },
  },

  components: {
    "project-data": projectData,
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期処理、Index読出し */
    init() {
      this.modal_requirement_138 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // Index取得
      this.executeReadIndex();

      const vehicle = this.vehicleInfo.vehicle;
      if (vehicle.vin.type === config.VEHICLEINFO_TYPE_VIN) {
        this.hideRegistrationNumber = false;
      }
    },

    /** Index取得 */
    async executeReadIndex() {
      try {
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read',
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // Indexデータ取得処理
              await this.readResultDisplay(response.data.user_data.result_data);
              // データ存在有無に対する分岐
              if (this.items.length) {
                // チェックボックスハンドリング用配列生成
                await this.generateCheckboxHandleList(this.items);
                // Index表示画面へ遷移
                this.phase = PHASE.INDEX_DISPLAY;
                break;
              } else {
                // データなし表示へ遷移
                this.phase = PHASE.DATA_NON_EXISTENCE;
              }
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 異常終了：エラー
              this.showUnexpectedError();
              return;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    // チェックボックスハンドリング用配列生成
    async generateCheckboxHandleList(items) {
      items.forEach(() => {
        this.isChecked = this.isChecked.concat(false);
        this.isDisabled = this.isDisabled.concat(false);
      });
    },

    /**
    * チェックボックスクリック処理（2個制限）
    */
    checkboxClicked(index) {
      // チェック情報更新
      this.isChecked[index] = !this.isChecked[index];

      // チェックされている番号のみcheckedNum配列に格納
      let checkedCnt = 0;
      let checkedNum = [];
      this.isChecked.forEach(element => {
        if (element !== false) {
          checkedNum = checkedNum.concat(checkedCnt);
        }
        checkedCnt++;
      });

      // 取得ボタン制御
      this.isButtonDisabled = checkedNum.length === 0;

      // チェックボックス非活性化処理
      if (checkedNum.length === LIMIT_NUM) {
        // チェックボックス2個チェック済み時処理
        let i = 0;
        this.isChecked.forEach(element => {
          if (element === false) {
            this.isDisabled[i] = true;
          } else {
            this.isDisabled[i] = false;
          }
          i++;
        });
      } else {
        // チェックボックス未チェック／1個チェック済み時処理
        let i = 0;
        this.isChecked.forEach(() => {
          this.isDisabled[i] = false;
          i++;
        });
      }
      // ログデータ取得要求用に配列を返す
      return checkedNum;
    },

    /** ログデータ取得処理 */
    async executeReadLog() {
      // 【実装用】チェック済みボックスからログデータ読出し対象のDID特定
      const tmpCheckedNumList = this.checkboxClicked();
      const selectedDid = [];
      for (let i = 0; i < tmpCheckedNumList.length; i++) {
        // 結果表示用配列の作成(this.checkedNumList)
        this.checkedNumList[i] = {
          resultDisplayNum: tmpCheckedNumList[i] + 1,
          did: this.existentDidList[tmpCheckedNumList[i]]
        };
        // upload APIリクエストパラメータ用配列作成(selectedDid)
        selectedDid.push(this.existentDidList[tmpCheckedNumList[i]]);
      }
      this.phase = PHASE.DATA_ACQUISITION;
      try {
        this.isLoading = true;
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'upload',
            did_list: selectedDid
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // アップロード結果をthis.checkedNumListに格納
              await this.getUploadResult(response.data.user_data.upload_results);
              this.phase = PHASE.DATA_RESULTS_DISPLAY;
              // requestIdを保持
              this.requestId = response.data.user_data.request_id;
              // ファイルダウンロード用
              this.dataName = response.data.user_data.data_name;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 異常終了：エラー
              this.showUnexpectedError();
              return;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * データアップロード結果をcheckedNumList配列に追加
     * @param {Object} responseData レスポンスデータ
    */
    async getUploadResult(responseData) {
      responseData.forEach(responseElement => {
        this.checkedNumList.forEach((requestElement, index) => {
          if (requestElement.did === responseElement.did) {
            requestElement.upload_status = responseElement.upload_status;
          }

          this.checkedNumList[index] = requestElement;
        });
      });
    },

    async callSaveEvent() {
      if (this.$refs.projectData.validate()) {
        // プロジェクトデータ保存・更新処理
        await this.saveUpdate();
        await this.closeWizard();
      }
    },

    /** プロジェクトデータ保存・更新処理 */
    async saveUpdate() {
      try {
        this.isLoading = true;

        let endPoint = '/projects/' + this.vehicleInfo.vehicle.project.id
          + '/netApps/' + this.netAppId
          + '/requests/' + this.requestId;

        const postReqBody = {
          storage_data: this.getStorageData()
        };

        // 更新準備 API
        const saveRes = await apiService.postCall(apiService.WSP, endPoint, postReqBody, this.operationScreen);

        let ret = false;
        // APIレスポンスの検証
        handler.validate(handler.validateTypes.all, saveRes, this, null, () => {
          ret = true;
        }, (result) => {
          // レスポンス不正時のコールバック
          if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
        });

        if (ret) {
          endPoint = '/projects/' + this.vehicleInfo.vehicle.project.id
            + '/' + saveRes.data.measured_id;
          const reqBody = {
            comment: this.$refs.projectData.inputDiagnoseComment,
            measured: {
              name: this.$refs.projectData.inputDataName,
              memo: this.$refs.projectData.inputMemo
            }
          };
          // 更新 API
          const updateRes = await apiService.postCall(apiService.WSP, endPoint, reqBody, this.operationScreen);
          // APIレスポンスの検証
          handler.validate(handler.validateTypes.all, updateRes, this, null, () => {
          }, (result) => {
            // レスポンス不正時のコールバック
            if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
          });
        }
      }
      finally {
        this.isLoading = false;
      }
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_138 = false;
    },

    /** その他エラーの表示 */
    showUnexpectedError() {
      this.phase = PHASE.ACQUISITION_FAILED;
    },

    /**
    * API 通信で共通バリデーションでエラーが発生した際のコールバック
    * @param {object} result handler.validate で返ってきたバリデーション結果
    */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
    * データテーブル表示用配列生成
    * @param {Array} resultData 読出しデータ
    */
    async readResultDisplay(resultData) {
      // タイムスタンプ絶対時間最小値の降順にソート
      resultData.sort((a, b) => a.period_min - b.period_min);
      let i = 0;
      for (const element of resultData) {
        if (element.data_existence) {
          // テーブル表示用
          this.items.push({
            "number": i + 1,
            "period": this.convertLocalDateTime(element.period_min) + " ~ " + this.convertLocalDateTime(element.period_max)
          });
          this.existentDidList[i] = element.did;
          i++;
        }
      }
    },

    /** 保存ボタン押下 プロジェクト保存画面遷移 */
    screenMovement() {
      const vehicle = this.vehicleInfo.vehicle;
      // プロジェクト保存画面に必要な情報を設定
      this.vin = vehicle.vin.vin;
      this.displayProjectID = vehicle.project.id;
      let spec = null;
      // 車両の設定
      switch (vehicle.vin.type) {
        case config.VEHICLEINFO_TYPE_VIN:
          spec = vehicle.spec.model_year ? vehicle.spec.model_year : '-';
          break;
        case config.VEHICLEINFO_TYPE_CHASSISNUMBER:
          spec = vehicle.spec.classification ? vehicle.spec.classification : '-';
          break;
        case config.VEHICLEINFO_TYPE_UN_KNOWN:
        default:
          spec = '-';
          break;
      }
      this.vehicleName = shareVehicle.createModelDisplayNameCurrentLanguage(this.vehicleInfo, this.vehicleModelDisplayNameList, this.language, '{0} {1}', spec);
      // 海外VINの場合、登録番号は画面から非表示にする
      if (this.hideRegistrationNumber) {
        if (vinInfoUtils.outputRegistrationNumber(vehicle.vin)) {
          this.registrationNumber = vinInfoUtils.createRegistrationNumber(vehicle.vin);
        }
        else {
          this.registrationNumber = "";
        }
      }
      this.phase = PHASE.PROJECT_DATA;
    },

    /**
    * テーブル列に適用するスタイルを指定
    * @param {Array} data 画面データ
    * @param String columnName カラム名
    */
    getColumnClasses(data, columnName) {
      if (!data.fieldSpec || !data.fieldSpec[columnName]) {
        return {};
      }
      const columnConfig = data.fieldSpec[columnName];
      return {
        'field-highlight': columnConfig === config.LIST_ITEM_HIGHLIGHT,
        'field-error': columnConfig === config.LIST_ITEM_ERROR,
        'field-bold': columnConfig === config.LIST_ITEM_BOLD || columnConfig === config.LIST_ITEM_HIGHLIGHT,
        'field-disable': columnConfig === config.LIST_ITEM_DISABLE
      };
    },

    /**
    * テーブル列に適用するスタイルを指定
    * @param String style メインフローから指定されるスタイル
    * @param int index カラム名
    */
    getTdStyle(style, index) {
      return { [style]: true, 'table-row-stripe': (index % 2 === 0) && true };
    },

    /**
     * storage_dataの情報を取得
     */
    getStorageData() {
      const storageData = [];
      this.checkedNumList.forEach(checkedItem => {
        this.items.forEach((readData) => {
          // 読み込んだデータとuploadの取得結果のindex番号を確認する
          if (checkedItem.resultDisplayNum === readData.number) {
            storageData.push({
              did: checkedItem.did,
              number: readData.number,
              timestamp: readData.period,
              upload_status: checkedItem.upload_status
            });
          }
        });
      });
      return storageData;
    },

    /**
     * GMTからLocalTimeへ変換
     * @param {*} responseDate 絶対時間タイプスタンプ情報
     */
    convertLocalDateTime(responseDate) {
      if (!responseDate) {
        return null;
      }

      // 0xFF埋め(20FF-FF-FFTFF:FF:FFZ)の場合、そのまま表示する
      if (responseDate === '20FF-FF-FFTFF:FF:FFZ') {
        return responseDate;
      }

      const globalDate = new Date(responseDate);
      // 日付として無効な文字列の場合Backendから帰ってきた場合、ハイフン'-'として表示する
      if (globalDate.toString() === 'Invalid Date') {
        return '-';
      }

      // LocalTimeに変換
      return util.timeConverter(globalDate);
    },
  },

  mounted() {
    this.init();
  },
};